import { ApolloLink, InMemoryCache } from '@apollo/client';
import type { FetchResult, Operation } from '@apollo/client/link/core';

import { getRecommendationCollectionMetadataFragment } from 'bundles/recommendations-common/lib/recommendationCollectionMetadata';

export const RECS_EXPLAINER_CAPTURE_EVENT = 'recsExplainerCapture';

let captureCache: InMemoryCache | undefined;

type CaptureCacheConfig = ConstructorParameters<typeof InMemoryCache>[0];

const createCaptureCache = (config: CaptureCacheConfig) => new InMemoryCache(config);

export function getRecsExplainerCaptureCache(): InMemoryCache | undefined {
  return captureCache;
}

export function resetRecsExplainerCaptureCache(): void {
  captureCache = undefined;
}

const captureRecommendationResult = (operation: Operation, result: FetchResult, config: CaptureCacheConfig): void => {
  try {
    if (
      typeof window === 'undefined' ||
      !result.data ||
      !getRecommendationCollectionMetadataFragment(operation.query)
    ) {
      return;
    }

    const nextCaptureCache = captureCache ?? createCaptureCache(config);
    nextCaptureCache.writeQuery({
      query: operation.query,
      variables: operation.variables,
      data: result.data,
    });
    captureCache = nextCaptureCache;
    window.dispatchEvent(new Event(RECS_EXPLAINER_CAPTURE_EVENT));
  } catch (error) {
    console.warn('Failed to capture a recommendation response for the explainer:', error);
  }
};

export function createRecsExplainerCaptureLink(config: CaptureCacheConfig): ApolloLink {
  return new ApolloLink((operation, forward) =>
    forward(operation).map((result) => {
      captureRecommendationResult(operation, result, config);
      return result;
    })
  );
}
